/**
 * 判断是否是真实的对象类型（内部函数）
 * 
 * 用于区分普通对象和数组、null 等。
 * 
 * @param value 要判断的值
 * @returns 是否为真实对象类型
 */
function isTrulyObject(value: any): boolean {
  return Object.prototype.toString.call(value) === '[object Object]'
}

/**
 * 判断基础类型是否相等（内部函数）
 * 
 * 比较两个基础类型值是否相等，包括处理 NaN 的情况。
 * 
 * @param value 第一个值
 * @param other 第二个值
 * @returns 是否相等
 */
function baseIsEqual(value: any, other: any): boolean {
  if (value === other) return true
  if (value == null || other == null) return false // 处理 null 和 undefined
  if (typeof value !== typeof other) return false
  return Number.isNaN(value) && Number.isNaN(other)
}

/**
 * 对象浅比较
 * @param value
 * @param other
 * @returns
 */
function objEqualShallow(value: any, other: any): boolean {
  if (value === other) return true // 处理相同引用
  if (value == null || other == null) return false // 处理 null 和 undefined
  const valueProps = Object.keys(value)
  const otherProps = Object.keys(other)
  if (valueProps.length !== otherProps.length) return false

  return valueProps.every((propName) => baseIsEqual(value[propName], other[propName]))
}

/**
 * 数组浅比较
 * @param value
 * @param other
 * @returns
 */
function arrayEqualShallow(value: any, other: any): boolean {
  if (value === other) return true // 处理相同引用
  if (value == null || other == null || value.length !== other.length) return false // 处理 null 和 undefined
  return value.every((v: any, i: number) => baseIsEqual(v, other[i]))
}

/**
 * 变量浅比较
 * @param value
 * @param other
 * @returns
 */
function isShallowEqual(value: any, other: any): boolean {
  if (baseIsEqual(value, other)) return true
  if (isTrulyObject(value) && isTrulyObject(other)) return objEqualShallow(value, other)
  if (Array.isArray(value) && Array.isArray(other)) return arrayEqualShallow(value, other)
  return false
}

/**
 * 对象深比较
 * @param value
 * @param other
 * @returns
 */
function isDeepEqual(value: any, other: any): boolean {
  if (baseIsEqual(value, other)) return true
  if (isTrulyObject(value) && isTrulyObject(other)) return objEqualDeep(value, other)
  if (Array.isArray(value) && Array.isArray(other)) return arrayEqualDeep(value, other)
  return false
}

/**
 * 数组深比较
 * @param value
 * @param other
 * @returns
 */
function arrayEqualDeep(value: any, other: any): boolean {
  if (value.length !== other.length) return false
  for (let i = 0, len = value.length; i < len; i++) {
    if (!isDeepEqual(value[i], other[i])) return false
  }
  return true
}

/**
 * 对象深比较
 * @param value
 * @param other
 * @returns
 */
function objEqualDeep(value: any, other: any): boolean {
  if (value === other) return true // 处理相同引用
  if (value == null || other == null) return false // 处理 null 和 undefined
  const valueProps = Object.keys(value)
  const otherProps = Object.keys(other)
  if (valueProps.length !== otherProps.length) return false

  return valueProps.every((propName) => isDeepEqual(value[propName], other[propName]))
}

/**
 * 对象比较。
 * @param value 第一个比较的值。
 * @param other 第二个比较的值。
 * @param options 可选参数，可选项：`deep` 是否深比较。
 * @returns {boolean} 是否相等。
 */
export function equal(value: any, other: any, opt = { deep: false }): boolean {
  if (!opt.deep) return isShallowEqual(value, other)
  return isDeepEqual(value, other)
}
